import { useEffect, useState } from "react";
import { ApolloError } from "@apollo/client";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useRequestEmailConfirmationLinkMutation } from "../../../generated/graphql";
import { colors } from "../../../stitches.config";
import { CenterContent } from "../../Common/__styles__/Layout";
import { Button } from "../../Common/Button";
import { Section, Sections } from "../../Common/Layout";
import { Body, Title } from "../../Common/Typography";
import Layout from "../../Guest/Layout";
import { Text } from "../../Inputs/react-hook-form";
import { Container as LoginContainer } from "../CommonLoginComponents/__styles__/FormWithFullPageBackground";
import { ErrorMessageBanner } from "../ErrorMessage";

type FormData = {
  email: string;
};

enum SCREEN {
  REQUEST_CONFIRMATION_CODE,
  CONFIRMATION_CODE_SENT,
}

const RequestConfirmationCodeScreen = ({
  onSubmit,
  loading,
  error,
}: {
  onSubmit: (data: FormData) => void;
  loading: boolean;
  error: ApolloError | undefined;
}) => {
  const { register, handleSubmit } = useForm<FormData>();
  const { t } = useTranslation();

  return (
    <Sections as="form" onSubmit={handleSubmit(onSubmit)}>
      <Section>
        {error && (
          <ErrorMessageBanner>
            {t("sign-in-unexpected-error")}
          </ErrorMessageBanner>
        )}
        <Title type="semiBold" size="large">
          {t("common-resend-code")}
        </Title>
        <Body size="default" type="regular">
          {t("resend-code-text")}
        </Body>
        <Text label={t("sign-in-email")} {...register("email")} />
      </Section>
      <Button
        type="submit"
        size="medium"
        styleVariant="primary"
        loading={loading}
        stretch
      >
        {t("common-resend-code")}
      </Button>
    </Sections>
  );
};

const ConfirmationCodeSentScreen = () => {
  const { t } = useTranslation();
  return (
    <Sections>
      <Section>
        <Title type="semiBold" size="large">
          {t("verify-your-account-header")}
        </Title>
        <Body size="default" type="regular">
          {t("verify-your-account-text")}
        </Body>
      </Section>
    </Sections>
  );
};

export const RequestEmailConfirmation = () => {
  const [screen, setScreen] = useState(SCREEN.REQUEST_CONFIRMATION_CODE);
  const [requestConfirmationCode, { loading: resendCodeLoading, data, error }] =
    useRequestEmailConfirmationLinkMutation();

  const handleFormData = (data: FormData) => {
    void requestConfirmationCode({ variables: { email: data.email } });
  };

  useEffect(() => {
    if (data?.requestEmailConfirmationLink) {
      setScreen(SCREEN.CONFIRMATION_CODE_SENT);
    }
  }, [data]);

  return (
    <Layout
      backgroundColor={colors.bgUiContainerInverted}
      footerFontColor="contentPrimaryInverted"
    >
      <CenterContent>
        <LoginContainer>
          {screen === SCREEN.REQUEST_CONFIRMATION_CODE && (
            <RequestConfirmationCodeScreen
              onSubmit={handleFormData}
              loading={resendCodeLoading}
              error={error}
            />
          )}
          {screen === SCREEN.CONFIRMATION_CODE_SENT && (
            <ConfirmationCodeSentScreen />
          )}
        </LoginContainer>
      </CenterContent>
    </Layout>
  );
};
